import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, isRef as _isRef, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "heartrate-module__select"
}

import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { deviceType } from '@/types/device'
import { useDebounceFn } from '@vueuse/core'
import { Icon } from '@iconify/vue'
import BaseModule from './BaseModule.vue'
import {
  NSlider,
  NInputNumber,
  NInputGroup,
  NInputGroupLabel,
  NAlert,
  NForm,
  NFormItem,
  NSelect,
} from 'naive-ui'
import { BfIcon, Colors } from '@finetic/bikefitting-ui'
import {
  RESISTANCE_POWER_TARGET_MIN,
  RESISTANCE_POWER_TARGET_MAX,
  RESISTANCE_POWER_TARGET_STEP,
  RESISTANCE_LEVEL_MIN,
  RESISTANCE_LEVEL_MAX,
  RESISTANCE_LEVEL_STEP,
} from '@/outputVariables'
import { ResistanceUnitMode } from '@/types/resistance'
import PairDevices from '@/components/controls/PairDevices.vue'
import ResistanceExternal from '@/components/controls/ResistanceExternal.vue'

//Store
import { useMqttStore } from '@/store/mqttStore'
import { useResistanceStore } from '@/store/resistanceStore'
import { useDeviceStore } from '@/store/deviceStore'
import { storeToRefs } from 'pinia'
import { ResistanceModalText } from '@/types/resistance'

//Mqtt
import {
  resistanceDefinitions,
  powerTarget,
  resistanceLevel,
} from '@/mqttDefinitions'
import { MqttMessage } from '@/mqttDefinitions'
import { sleep } from '@/utils/utils'


export default /*@__PURE__*/_defineComponent({
  __name: 'ResistanceModule',
  setup(__props) {

const { t } = useI18n()

const mqttStore = useMqttStore()
const resistanceStore = useResistanceStore()
const deviceStore = useDeviceStore()
const { doPublish } = mqttStore
const { client } = storeToRefs(mqttStore)
const { mode } = storeToRefs(resistanceStore)
const { resistanceWheelStatus } = storeToRefs(deviceStore)
const { doDevicesRefresh } = useDeviceStore()

const changeDevice = ref(false)
const externalModal = ref(false)
const modalText = ref(ResistanceModalText.ENTER)

//Data init
const currentPowerTarget = ref(RESISTANCE_POWER_TARGET_MIN)
const currentResistanceLevel = ref(RESISTANCE_LEVEL_MIN)

const modeOptions = ref([
  {
    label: t('modules.resistance.powerTarget'),
    value: ResistanceUnitMode.POWER_TARGET,
  },
  {
    label: t('modules.resistance.resistanceLevel'),
    value: ResistanceUnitMode.RESISTANCE_LEVEL,
  },
  {
    label: t('modules.resistance.external.option'),
    value: ResistanceUnitMode.EXTERNAL,
  },
])

const setPowerTargetValue = useDebounceFn(() => {
  doPublish(
    {
      topic: powerTarget.topic,
      payload: currentPowerTarget.value,
    } as MqttMessage,
    powerTarget,
    client.value,
  )
}, 200)

const setResistanceLevelValue = useDebounceFn(() => {
  doPublish(
    {
      topic: resistanceLevel.topic,
      payload: currentResistanceLevel.value,
    } as MqttMessage,
    resistanceLevel,
    client.value,
  )
}, 200)

const powerTargetValue = computed({
  get() {
    return currentPowerTarget.value
  },
  set(newValue) {
    currentPowerTarget.value = newValue
    setPowerTargetValue()
  },
})

const resistanceLevelValue = computed({
  get() {
    return currentResistanceLevel.value
  },
  set(newValue) {
    currentResistanceLevel.value = newValue
    setResistanceLevelValue()
  },
})

const closeModal = () => {
  externalModal.value = false
  setMode()
}

const setMode = async () => {
  if (mode.value === ResistanceUnitMode.POWER_TARGET) {
    doDevicesRefresh()
    await sleep(3000)
    powerTargetValue.value = 0
  } else if (mode.value === ResistanceUnitMode.RESISTANCE_LEVEL) {
    doDevicesRefresh()
    await sleep(3000)
    resistanceLevelValue.value = 0
  }
}

watch(mode, (newValue, oldValue) => {
  switch (newValue) {
    case ResistanceUnitMode.POWER_TARGET:
      if (oldValue === ResistanceUnitMode.EXTERNAL) {
        externalModal.value = true
        modalText.value = ResistanceModalText.LEAVE
      } else {
        setMode()
      }
      break

    case ResistanceUnitMode.RESISTANCE_LEVEL:
      if (oldValue === ResistanceUnitMode.EXTERNAL) {
        externalModal.value = true
        modalText.value = ResistanceModalText.LEAVE
      } else {
        setMode()
      }
      break

    case ResistanceUnitMode.EXTERNAL:
      externalModal.value = true
      modalText.value = ResistanceModalText.ENTER
      break
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseModule, {
    definition: _unref(resistanceDefinitions),
    connected: _unref(resistanceWheelStatus).connected,
    connecting: _unref(resistanceWheelStatus).connecting,
    "content-active": 
      _unref(resistanceWheelStatus).connected && !_unref(resistanceWheelStatus).connecting
    
  }, {
    title: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)('modules.resistance.index')), 1)
    ]),
    config: _withCtx(() => [
      _createElementVNode("div", {
        class: "resistance-module__settings",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (changeDevice.value = true))
      }, [
        _createVNode(_unref(BfIcon), {
          icon: "/icons/01-Interface Essential/12-Settings/cog.svg",
          color: _unref(Colors).Light
        }, null, 8, ["color"])
      ])
    ]),
    middle: _withCtx(() => [
      (_unref(mode) === 'powerTarget')
        ? (_openBlock(), _createBlock(_unref(NSlider), {
            key: 0,
            value: powerTargetValue.value,
            "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((powerTargetValue).value = $event)),
            step: _unref(RESISTANCE_POWER_TARGET_STEP),
            min: _unref(RESISTANCE_POWER_TARGET_MIN),
            max: _unref(RESISTANCE_POWER_TARGET_MAX)
          }, null, 8, ["value", "step", "min", "max"]))
        : _createCommentVNode("", true),
      (_unref(mode) === 'resistanceLevel')
        ? (_openBlock(), _createBlock(_unref(NSlider), {
            key: 1,
            value: resistanceLevelValue.value,
            "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((resistanceLevelValue).value = $event)),
            step: _unref(RESISTANCE_LEVEL_STEP),
            min: _unref(RESISTANCE_LEVEL_MIN),
            max: _unref(RESISTANCE_LEVEL_MAX)
          }, null, 8, ["value", "step", "min", "max"]))
        : _createCommentVNode("", true),
      (_unref(mode) === 'external')
        ? (_openBlock(), _createBlock(_unref(NAlert), {
            key: 2,
            title: _unref(t)('modules.resistance.external.option'),
            bordered: false
          }, {
            icon: _withCtx(() => [
              _createVNode(_unref(Icon), {
                icon: "mdi:information-outline",
                style: { fontSize: '24px' }
              })
            ]),
            default: _withCtx(() => [
              _createElementVNode("p", null, _toDisplayString(_unref(t)('modules.resistance.external.noControls')), 1)
            ]),
            _: 1
          }, 8, ["title"]))
        : _createCommentVNode("", true)
    ]),
    bottom: _withCtx(() => [
      _createVNode(_unref(NForm), {
        ref: "formRef",
        "label-placement": "left"
      }, {
        default: _withCtx(() => [
          (_unref(mode) === 'powerTarget')
            ? (_openBlock(), _createBlock(_unref(NFormItem), {
                key: 0,
                class: "resistance-module__input",
                label: _unref(t)('modules.resistance.powerTarget')
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(NInputGroup), null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(NInputNumber), {
                        value: powerTargetValue.value,
                        "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((powerTargetValue).value = $event)),
                        step: _unref(RESISTANCE_POWER_TARGET_STEP),
                        min: _unref(RESISTANCE_POWER_TARGET_MIN),
                        max: _unref(RESISTANCE_POWER_TARGET_MAX),
                        "button-placement": "both"
                      }, null, 8, ["value", "step", "min", "max"]),
                      _createVNode(_unref(NInputGroupLabel), null, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(powerTarget).unit), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["label"]))
            : _createCommentVNode("", true),
          (_unref(mode) === 'resistanceLevel')
            ? (_openBlock(), _createBlock(_unref(NFormItem), {
                key: 1,
                class: "resistance-module__input",
                label: _unref(t)('modules.resistance.resistanceLevel')
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(NInputGroup), null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(NInputNumber), {
                        value: resistanceLevelValue.value,
                        "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((resistanceLevelValue).value = $event)),
                        step: _unref(RESISTANCE_LEVEL_STEP),
                        min: _unref(RESISTANCE_LEVEL_MIN),
                        max: _unref(RESISTANCE_LEVEL_MAX),
                        "button-placement": "both"
                      }, null, 8, ["value", "step", "min", "max"]),
                      _createVNode(_unref(NInputGroupLabel), null, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(resistanceLevel).unit), 1)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }, 8, ["label"]))
            : _createCommentVNode("", true),
          _createVNode(_unref(NFormItem), null, {
            default: _withCtx(() => [
              _createVNode(_unref(NSelect), {
                value: _unref(mode),
                "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => (_isRef(mode) ? (mode).value = $event : null)),
                options: modeOptions.value
              }, null, 8, ["value", "options"])
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 512),
      (changeDevice.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(PairDevices, {
              type: _unref(deviceType).resistanceWheel,
              onClose: _cache[6] || (_cache[6] = ($event: any) => (changeDevice.value = false))
            }, null, 8, ["type"])
          ]))
        : _createCommentVNode("", true),
      (externalModal.value)
        ? (_openBlock(), _createBlock(ResistanceExternal, {
            key: 1,
            text: modalText.value,
            onClose: closeModal
          }, null, 8, ["text"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["definition", "connected", "connecting", "content-active"]))
}
}

})